import { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import useOffers from 'api/useOffers';
import type { Offer } from 'common/contracts';
import { OfferType, SortingOffersValue } from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';
import { useTableSorting } from 'hooks/useTableSorting';
import { statusUtils } from 'utils/scheduleUtils';

import type { ProgressBarRowData } from '../../ProgressBars.types';

type OffersHookReturn = ReturnType<typeof useOffers>;

interface IUseProgressBarsDataReturn {
  filteredData: ProgressBarRowData[];
  isLoading: boolean;
  isEmpty: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  rows: number;
  skuFilterValue: string;
  setSkuFilterValue: (value: string) => void;
  statusFilterValue: string;
  setStatusFilterValue: (value: string) => void;
  refetch: () => void;
  deleteOffer: OffersHookReturn['deleteOffer'];
  toggleOfferStatus: OffersHookReturn['toggleOfferStatus'];
}

/**
 * Hook that manages data fetching, pagination, filtering, and URL synchronization for the Progress Bars table.
 *
 * @example
 * const { filteredData, isLoading, currentPage, setCurrentPage } = useProgressBarsData();
 */
export const useProgressBarsData = (): IUseProgressBarsDataReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { page, rows } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const { sorting } = useTableSorting(SortingOffersValue.CREATED_AT);
  const [skuFilterValue, setSkuFilterValue] = useState<string>('');
  const [statusFilterValue, setStatusFilterValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { getOffers, deleteOffer, toggleOfferStatus } = useOffers({
    offerId: undefined,
    offerType: OfferType.PROGRESS_BAR,
    customPagination: { currentPage, rowsPerPage: rows },
    sorting: {
      sortValue: sorting.sortValue ?? SortingOffersValue.CREATED_AT,
      direction: sorting.direction
    }
  });

  useEffect(() => {
    setIsLoading(
      getOffers.isFetching || getOffers.isRefetching || getOffers.isLoading
    );
  }, [getOffers.data?.offers, getOffers.isRefetching]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rows));
    if (sorting.sortValue !== null) {
      queryParams.set('sortValue', sorting.sortValue);
      queryParams.set('direction', sorting.direction);
    }

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
  }, [currentPage, rows, sorting]);

  const isEmpty = useMemo(
    (): boolean => getOffers.data?.offers?.length === 0,
    [getOffers.data?.offers]
  );

  const totalCount = useMemo(
    (): number => getOffers.data?.totalCount || 0,
    [getOffers.data?.totalCount]
  );

  const totalPages = useMemo(
    (): number => Math.ceil(totalCount / rows),
    [totalCount, rows]
  );

  const filteredData = useMemo((): ProgressBarRowData[] => {
    if (!getOffers?.data?.offers) return [];

    return getOffers.data.offers.filter((offer: Offer) => {
      const offerStatus = statusUtils.getOfferStatus(offer, offer.active);

      const isSkuMatch = offer.publisherOfferId
        .toLowerCase()
        .includes(skuFilterValue.toLowerCase());

      const isStatusMatch =
        !statusFilterValue ||
        statusFilterValue === 'All' ||
        statusFilterValue === offerStatus;

      return isSkuMatch && isStatusMatch;
    });
  }, [getOffers?.data?.offers, skuFilterValue, statusFilterValue]);

  return {
    filteredData,
    isLoading,
    isEmpty,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    rows,
    skuFilterValue,
    setSkuFilterValue,
    statusFilterValue,
    setStatusFilterValue,
    refetch: getOffers.refetch,
    deleteOffer,
    toggleOfferStatus
  };
};
